/**
 * Что заглушено: сервер, канал, его категория (у ветки — ещё и её канал) и личные беседы.
 * Правила те же, что у push на сервере (backend/internal/push/handler.go): mute категории
 * глушит все каналы в ней, срок mute_config.end_time прошёл — mute больше не действует.
 */
import { ChannelType, type Channel, type ChannelOverride, type Message, type UserGuildSettings } from '../types/api'

/** Ключ записи личных бесед в сторе настроек: у Discord у неё guild_id = null, в API — @me. */
export const DM_SETTINGS = '@me'

// Уровни уведомлений — как у Discord.
export const NOTIFY_ALL = 0
export const NOTIFY_MENTIONS = 1
export const NOTIFY_NOTHING = 2
export const NOTIFY_INHERIT = 3

function active(muted: boolean | undefined, end: string | null | undefined, now: number): boolean {
  if (!muted) return false
  return !end || new Date(end).getTime() > now
}

/** Заглушен ли сервер (с учётом срока). */
export function guildMuted(s: UserGuildSettings | undefined, now = Date.now()): boolean {
  return active(s?.muted, s?.mute_config?.end_time, now)
}

export function overrideOf(s: UserGuildSettings | undefined, channelId: string): ChannelOverride | undefined {
  return s?.channel_overrides.find((o) => o.channel_id === channelId)
}

/** Заглушен ли сам канал или беседа — без родителей и сервера. */
export function ownMuted(s: UserGuildSettings | undefined, channelId: string, now = Date.now()): boolean {
  const o = overrideOf(s, channelId)
  return active(o?.muted, o?.mute_config?.end_time, now)
}

/**
 * Кто в цепочке «канал → родитель → категория» заглушен первым: сам канал, категория или,
 * у ветки, её канал. null — ни канал, ни его родители не заглушены (сервер не в счёт).
 */
export function mutedAncestor(s: UserGuildSettings | undefined, channelId: string, channels?: Record<string, Channel>, now = Date.now()): string | null {
  let id: string | null | undefined = channelId
  // ветка → канал → категория: глубже трёх уровней в Discord не бывает
  for (let depth = 0; id && depth < 3; depth++) {
    if (ownMuted(s, id, now)) return id
    id = channels?.[id]?.parent_id
  }
  return null
}

/** Заглушен ли канал: mute сервера, свой или родителя. Без карты каналов — только сервер и свой. */
export function channelMuted(s: UserGuildSettings | undefined, channelId: string, channels?: Record<string, Channel>, now = Date.now()): boolean {
  return guildMuted(s, now) || mutedAncestor(s, channelId, channels, now) !== null
}

/** Уровень уведомлений канала у пользователя: свой, иначе как на сервере (NOTIFY_INHERIT — не выбран). */
export function channelLevel(s: UserGuildSettings | undefined, channelId: string): number {
  return overrideOf(s, channelId)?.message_notifications ?? NOTIFY_INHERIT
}

/** Уровень сервера для пользователя: его выбор, иначе умолчание сервера. */
export function guildLevel(s: UserGuildSettings | undefined, guildDefault: number | undefined): number {
  if (s && s.message_notifications !== NOTIFY_INHERIT) return s.message_notifications
  return guildDefault ?? NOTIFY_ALL
}

/** Непрочитанные упоминания в личных беседах без заглушённых — для значка «Домой» и счётчика окна. */
export function dmMentions(readStates: Record<string, { mention_count: number }>, isDM: (channelId: string) => boolean, dm: UserGuildSettings | undefined, now = Date.now()): number {
  let n = 0
  for (const [id, rs] of Object.entries(readStates)) {
    if (rs.mention_count && isDM(id) && !ownMuted(dm, id, now)) n += rs.mention_count
  }
  return n
}

/** У темы форума свой режим; без переопределений подписка даёт все ответы. */
export function messageNotificationLevel(s: UserGuildSettings | undefined, channel: Channel | undefined, channels: Record<string, Channel> | undefined, guildDefault: number | undefined): number {
  const own = channel ? channelLevel(s, channel.id) : NOTIFY_INHERIT
  if (own !== NOTIFY_INHERIT) return own
  const parent = channel?.parent_id ? channels?.[channel.parent_id] : undefined
  if (channel?.type === ChannelType.PublicThread && parent?.type === ChannelType.GuildForum) {
    const forum = channelLevel(s, parent.id)
    if (forum !== NOTIFY_INHERIT) return forum
    if (s && s.message_notifications !== NOTIFY_INHERIT) return s.message_notifications
    return channel.member ? NOTIFY_ALL : NOTIFY_MENTIONS
  }
  return guildLevel(s, guildDefault)
}

/** Счётчик упоминаний не зависит от режима доставки уведомлений. */
export function messageMentionsUser(msg: Message, userId: string, s: UserGuildSettings | undefined, roles: string[]): boolean {
  return msg.mentions.some(user => user.id === userId)
    || Boolean(msg.mention_everyone && !s?.suppress_everyone)
    || Boolean(!s?.suppress_roles && msg.mention_roles?.some(role => roles.includes(role)))
}
